import { BlankSlateAIScan } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";

/** Props Interface */
interface ImportOfferFilesModalProps extends ModalProps {
	/** The files to raise a calculation from; the server adds a GAEB counterpart beside them. */
	fileIds: string[];
	/** Called once the calculation exists, before the editor is opened. */
	onComplete?: () => void | Promise<void>;
}

/**
 * The wait while an already-stored Angebot is read into a calculation.
 *
 * A modal rather than a menu item that spins, because the wait is the reason it exists: a PDF goes
 * through three model passes and takes tens of seconds, and the two surfaces that open this — the
 * file inbox and a project's document folder — are both lists somebody would otherwise keep
 * clicking in while it ran.
 *
 * **The editor is navigated to rather than opened in a tab**, which is the one place this differs
 * from every other way into a calculation. `window.open` half a minute after the click is no
 * longer attached to that click, and a browser blocks it as a popup — so the import would finish
 * with nothing to show for it.
 */
export function ImportOfferFilesModal({
	isOpen,
	onClose,
	fileIds,
	onComplete,
}: ImportOfferFilesModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const navigate = useNavigate();
	// React runs an effect twice in development and the import is neither cheap nor idempotent —
	// a second run would raise a second calculation from the same Angebot.
	const hasStarted = useRef(false);
	// Dismissing the wait is a refusal to be taken to the editor, not a cancellation: the request
	// is already with the server and the calculation will exist. So the answer is reported where
	// the user still is, and the list is where they reach it.
	const isDismissed = useRef(false);

	/** Functions */
	async function handleOnImport() {
		try {
			const response = await client.calculations.offers.files.$post({ json: { fileIds } });
			const data = await responseHandler(response);
			if (!data.calculationId) throw new Error(t("calculations.offer_import_failed"));
			await onComplete?.();
			if (isDismissed.current) {
				toast.success(t("calculations.offer_imported"));
				return;
			}
			navigate(`/calculations/${data.calculationId}/editor?limiter=external`);
		} catch (error) {
			toast.error((error as Error).message);
		} finally {
			onClose();
		}
	}

	function handleOnDismiss() {
		isDismissed.current = true;
		onClose();
	}

	/** Effects */
	useEffect(() => {
		if (hasStarted.current) return;
		hasStarted.current = true;
		handleOnImport();
	}, []);

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={handleOnDismiss}
			title={t("calculations.importing_offer")}
		>
			<Modal.Body>
				<BlankSlateAIScan />
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={handleOnDismiss}>{t("general.close")}</Button>
			</Modal.Footer>
		</Modal>
	);
}
